<script setup>
import { ref, computed, watch, nextTick, useId } from 'vue'
import { onClickOutside, useMediaQuery, useScrollLock } from '@vueuse/core'
import Money from './Money.vue'
import { useSearchSuggestions, MIN_QUERY_LENGTH } from './search/useSearchSuggestions.js'
import { useRecentSearches } from './search/useRecentSearches.js'
import { highlight } from './search/highlight.js'

const MAX_CATEGORIES = 4
const MAX_BUNDLES = 6

const props = defineProps({
    placeholder: {
        type: String,
        default: 'Zoek op cursus of trefwoord',
    },
    // `lg` is the bigger variant with a labelled button, for the homepage hero
    size: {
        type: String,
        default: 'md',
    },
})

const isLarge = computed(() => props.size === 'lg')

// { title, items } from the `autocomplete` DataItem
const popular = window.CursusGeregeld.autocomplete?.popular ?? { title: 'Populaire zoekopdrachten', items: [] }

const baseId = useId()
const inputId = `${baseId}-input`
const listboxId = `${baseId}-listbox`

const root = ref(null)
const input = ref(null)

const query = ref('')
const isOpen = ref(false)
const activeIndex = ref(-1)

const { categories, bundles, loading, error, resultsFor } = useSearchSuggestions(query)
const { recent, remember, clear: clearRecent } = useRecentSearches()

const trimmedQuery = computed(() => query.value.trim())
const hasQuery = computed(() => trimmedQuery.value.length >= MIN_QUERY_LENGTH)
const hasResults = computed(() => categories.value.length > 0 || bundles.value.length > 0)
// Results are in, but for a query that is still being typed
const isStale = computed(() => loading.value && hasResults.value)
const isFirstLoad = computed(() => loading.value && !hasResults.value)
const hasNoResults = computed(() => hasQuery.value && !loading.value && !error.value && resultsFor.value === trimmedQuery.value && !hasResults.value)

function searchUrl (value) {
    return `/zoeken?f[q]=${encodeURIComponent(value)}`
}

// One flat, indexed list of options (for the arrow keys), grouped into sections (for the eye)
const sections = computed(() => {
    const list = []

    if (!hasQuery.value) {
        if (recent.value.length > 0) {
            list.push({ key: 'recent', title: 'Recent gezocht', options: recent.value.map(label => ({ kind: 'recent', label, url: searchUrl(label) })) })
        }
    } else {
        if (categories.value.length > 0) {
            list.push({ key: 'categories', title: 'Categorieën', options: categories.value.slice(0, MAX_CATEGORIES).map(category => ({ kind: 'category', label: category.name, url: category.path })) })
        }
        if (bundles.value.length > 0) {
            list.push({ key: 'bundles', title: 'Cursussen', options: bundles.value.slice(0, MAX_BUNDLES).map(bundle => ({ kind: 'bundle', label: bundle.name, url: bundle.path, price: bundle.price })) })
        }
        list.push({ key: 'all', title: null, options: [{ kind: 'all', label: trimmedQuery.value, url: searchUrl(trimmedQuery.value) }] })
    }

    // Popular searches help with an empty field, and when nothing matched
    if ((!hasQuery.value || hasNoResults.value) && popular.items?.length > 0) {
        list.push({ key: 'popular', title: popular.title, options: popular.items.map(label => ({ kind: 'popular', label })) })
    }

    let index = 0

    return list.map(section => ({
        ...section,
        options: section.options.map(option => ({ ...option, index, id: `${baseId}-option-${index++}` })),
    }))
})

const options = computed(() => sections.value.flatMap(section => section.options))
const activeOption = computed(() => options.value[activeIndex.value] ?? null)

const showPanel = computed(() => isOpen.value && (hasQuery.value || options.value.length > 0))

// A new list means a new selection
watch(options, () => { activeIndex.value = -1 })

// ─── Opening and closing ─────────────────────────────────────────────────────

function open () {
    isOpen.value = true
}

function close () {
    isOpen.value = false
    activeIndex.value = -1
}

onClickOutside(root, close)

function onFocusOut (event) {
    if (!root.value?.contains(event.relatedTarget)) {
        close()
    }
}

// On a phone the panel covers the page; keep the page behind it from scrolling along
const isSmall = useMediaQuery('(max-width: 47.99rem)')
const scrollLock = useScrollLock(typeof document !== 'undefined' ? document.body : null)
watch(() => showPanel.value && isSmall.value, value => { scrollLock.value = value })

// ─── Choosing ────────────────────────────────────────────────────────────────

function go (url, rememberedQuery) {
    remember(rememberedQuery)
    close()
    window.location.href = url
}

function choose (option) {
    if (option.kind === 'popular') {
        query.value = option.label
        input.value?.focus()
        return
    }

    go(option.url, option.kind === 'recent' ? option.label : trimmedQuery.value)
}

// Links keep working as links (middle click, open in new tab); a plain click also remembers the query
function onOptionClick (option, event) {
    if (option.kind === 'popular') {
        event.preventDefault()
        choose(option)
        return
    }

    if (!event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        remember(option.kind === 'recent' ? option.label : trimmedQuery.value)
    }
}

function submit () {
    if (activeOption.value) {
        choose(activeOption.value)
        return
    }

    if (!trimmedQuery.value) {
        input.value?.focus()
        return
    }

    go(searchUrl(trimmedQuery.value), trimmedQuery.value)
}

function clearQuery () {
    query.value = ''
    input.value?.focus()
    open()
}

// ─── Keyboard ────────────────────────────────────────────────────────────────

function move (step) {
    open()

    const count = options.value.length
    if (count === 0) return

    activeIndex.value = activeIndex.value === -1
        ? (step > 0 ? 0 : count - 1)
        : (activeIndex.value + step + count) % count

    nextTick(() => document.getElementById(activeOption.value?.id)?.scrollIntoView({ block: 'nearest' }))
}

function onKeydown (event) {
    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault()
            move(1)
            break
        case 'ArrowUp':
            event.preventDefault()
            move(-1)
            break
        case 'Escape':
            // First close the suggestions, then clear the field
            if (showPanel.value) {
                event.preventDefault()
                close()
            } else if (query.value) {
                query.value = ''
            }
            break
        case 'Tab':
            close()
            break
    }
}

// Lets the header focus the field when the search is unfolded on mobile
defineExpose({
    focus: () => input.value?.focus(),
})
</script>

<template>
    <div ref="root" class="relative" :class="{ 'z-50': showPanel }" @focusout="onFocusOut">

        <Transition
            enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
            enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
            leave-to-class="opacity-0"
        >
            <div v-if="showPanel" class="fixed inset-0 bg-gray-900/30" aria-hidden="true"></div>
        </Transition>

        <form role="search" action="/zoeken" method="get" class="relative" @submit.prevent="submit">
            <div
                class="flex items-center gap-1 bg-white p-1 shadow-sm ring-1 ring-black/5 transition-[border-radius] duration-150"
                :class="[
                    isLarge ? 'p-1.5 shadow-xl' : '',
                    showPanel ? (isLarge ? 'rounded-t-[1.875rem]' : 'rounded-t-[1.625rem]') : (isLarge ? 'rounded-[1.875rem]' : 'rounded-[1.625rem]'),
                ]"
            >
                <label :for="inputId" class="sr-only">Zoek een cursus</label>
                <input
                    :id="inputId"
                    ref="input"
                    v-model="query"
                    type="search"
                    name="f[q]"
                    role="combobox"
                    autocomplete="off"
                    autocapitalize="off"
                    spellcheck="false"
                    enterkeyhint="search"
                    aria-autocomplete="list"
                    :aria-expanded="showPanel"
                    :aria-controls="listboxId"
                    :aria-activedescendant="activeOption?.id"
                    :placeholder="placeholder"
                    class="min-w-0 grow bg-transparent pl-5 pr-2 text-base md:text-lg text-gray-900 placeholder:text-gray-500 outline-none [&::-webkit-search-cancel-button]:hidden"
                    :class="isLarge ? 'py-3' : 'py-2'"
                    @focus="open"
                    @input="open"
                    @keydown="onKeydown"
                />
                <button
                    v-if="query"
                    type="button"
                    class="grid size-9 flex-none place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                    aria-label="Zoekopdracht wissen"
                    @click="clearQuery"
                >
                    <icon name="cross" class="size-4"></icon>
                </button>
                <button
                    type="submit"
                    class="flex flex-none items-center justify-center gap-2 rounded-full bg-red-500 text-white transition-colors hover:bg-red-600 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                    :class="isLarge ? 'size-12 sm:w-auto sm:px-6' : 'size-11'"
                    aria-label="Zoeken"
                >
                    <svg v-if="loading && hasQuery" class="size-5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <icon v-else name="search" class="size-6"></icon>
                    <span v-if="isLarge" class="hidden sm:inline font-semibold">Zoeken</span>
                </button>
            </div>

            <!-- Suggestions -->
            <div
                v-show="showPanel"
                class="absolute inset-x-0 top-full overflow-hidden bg-white shadow-2xl ring-1 ring-black/5"
                :class="isLarge ? 'rounded-b-[1.875rem]' : 'rounded-b-[1.625rem]'"
            >
                <div
                    class="max-h-[min(32rem,calc(100dvh-12rem))] overflow-y-auto overscroll-contain border-t border-gray-100 p-2 transition-opacity"
                    :class="{ 'opacity-60': isStale }"
                >
                    <!-- First results for a query are on their way -->
                    <div v-if="isFirstLoad && hasQuery" class="space-y-1 p-1" aria-hidden="true">
                        <div v-for="n in 3" :key="n" class="flex items-center gap-3 rounded-xl px-3 py-2 animate-pulse">
                            <span class="size-9 flex-none rounded-lg bg-gray-100"></span>
                            <span class="h-3 rounded bg-gray-100" :style="{ width: `${70 - n * 12}%` }"></span>
                        </div>
                    </div>

                    <p v-if="hasNoResults" class="px-3 pt-3 pb-1 text-sm text-gray-600" role="status">
                        Geen suggesties voor <strong class="font-semibold text-gray-900">“{{ trimmedQuery }}”</strong>. Zoek toch in alle cursussen, of probeer een van deze:
                    </p>
                    <p v-if="error && hasQuery" class="px-3 pt-3 pb-1 text-sm text-gray-600" role="status">
                        Suggesties laden lukt nu even niet. Je kunt wel gewoon zoeken.
                    </p>

                    <ul :id="listboxId" role="listbox" aria-label="Zoeksuggesties">
                        <!-- "All results" sticks to the bottom, so it stays in view however many suggestions there are -->
                        <li
                            v-for="section in sections"
                            :key="section.key"
                            role="presentation"
                            :class="{
                                'sticky -bottom-2 -mx-2 mt-1 border-t border-gray-100 bg-white px-2 pt-1 pb-2': section.key === 'all',
                                'mt-1 border-t border-gray-100 pt-1': section.key === 'popular' && hasQuery,
                            }"
                        >

                            <div v-if="section.title" class="flex items-center justify-between px-3 pt-3 pb-1.5" role="presentation">
                                <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ section.title }}</span>
                                <button
                                    v-if="section.key === 'recent'"
                                    type="button"
                                    tabindex="-1"
                                    class="text-xs font-medium text-gray-400 hover:text-gray-700 cursor-pointer"
                                    @mousedown.prevent
                                    @click="clearRecent"
                                >Wissen</button>
                            </div>

                            <!-- Popular searches as chips -->
                            <ul v-if="section.key === 'popular'" role="group" :aria-label="section.title" class="flex flex-wrap gap-2 px-3 pt-1 pb-3">
                                <li
                                    v-for="option in section.options"
                                    :id="option.id"
                                    :key="option.id"
                                    role="option"
                                    :aria-selected="option.index === activeIndex"
                                    class="rounded-full px-4 py-1.5 text-sm font-medium cursor-pointer transition-colors"
                                    :class="option.index === activeIndex ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
                                    @mousedown.prevent
                                    @mousemove="activeIndex = option.index"
                                    @click="onOptionClick(option, $event)"
                                >{{ option.label }}</li>
                            </ul>

                            <ul v-else role="group" :aria-label="section.title ?? 'Alle resultaten'">
                                <li
                                    v-for="option in section.options"
                                    :id="option.id"
                                    :key="option.id"
                                    role="option"
                                    :aria-selected="option.index === activeIndex"
                                    @mousedown.prevent
                                    @mousemove="activeIndex = option.index"
                                >
                                    <a
                                        :href="option.url"
                                        tabindex="-1"
                                        class="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors"
                                        :class="option.index === activeIndex ? 'bg-gray-100' : ''"
                                        @click="onOptionClick(option, $event)"
                                    >
                                        <span
                                            class="grid size-9 flex-none place-items-center rounded-lg"
                                            :class="{
                                                'bg-red-50 text-red-500': option.kind === 'category',
                                                'bg-gray-100 text-gray-500': option.kind === 'bundle' || option.kind === 'recent',
                                                'bg-red-500 text-white': option.kind === 'all',
                                            }"
                                        >
                                            <icon :name="{ category: 'DashboardBrowsingIcon', bundle: 'study', recent: 'clock', all: 'search' }[option.kind]" class="size-5"></icon>
                                        </span>

                                        <span class="min-w-0 grow text-sm text-gray-700">
                                            <template v-if="option.kind === 'all'">
                                                <span class="font-semibold text-gray-900">Alle resultaten voor “{{ option.label }}”</span>
                                            </template>
                                            <template v-else-if="option.kind === 'recent'">{{ option.label }}</template>
                                            <template v-else>
                                                <template v-for="(part, partIndex) in highlight(option.label, trimmedQuery)" :key="partIndex">
                                                    <strong v-if="part.match" class="font-semibold text-gray-900">{{ part.text }}</strong>
                                                    <template v-else>{{ part.text }}</template>
                                                </template>
                                            </template>
                                        </span>

                                        <span v-if="option.kind === 'bundle' && parseInt(option.price?.amount) > 0" class="flex-none text-right text-xs text-gray-500">
                                            vanaf <money :money="option.price" class="text-sm font-semibold text-gray-900"></money>
                                        </span>
                                        <icon
                                            v-else-if="option.kind !== 'bundle'"
                                            name="ArrowRight"
                                            class="size-4 flex-none text-gray-400 transition-opacity"
                                            :class="option.index === activeIndex ? 'opacity-100' : 'opacity-0'"
                                        ></icon>
                                    </a>
                                </li>
                            </ul>

                        </li>
                    </ul>

                </div>

                <p class="hidden md:flex items-center gap-4 border-t border-gray-100 bg-gray-50 px-5 py-2 text-xs text-gray-400" aria-hidden="true">
                    <span><kbd class="rounded border border-gray-200 bg-white px-1 font-sans">↑</kbd> <kbd class="rounded border border-gray-200 bg-white px-1 font-sans">↓</kbd> kiezen</span>
                    <span><kbd class="rounded border border-gray-200 bg-white px-1 font-sans">Enter</kbd> openen</span>
                    <span><kbd class="rounded border border-gray-200 bg-white px-1 font-sans">Esc</kbd> sluiten</span>
                </p>
            </div>
        </form>
    </div>
</template>
